import React, { useState, useEffect, useMemo, useReducer, useCallback, useRef } from "react";
import Alert from "@mui/material/Alert";
import DtvGrid from "@directv-digital/dds.ui.dtv-grid";
import {
  Analytics,
  LINK_POSITION_BODY,
  fireCustomLinkClickImpressions,
  fireGa4ProductImpression,
  fireLinkClickEvent,
  fireCustomLinkClickGA,
  firePageLoadEvent,
  fireSearchImpressions
} from "@directv-digital/dds.services.analytics-service";
import DtvTheme, { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import { useBreakPoints, APPLICATION_NAME, PAGE_FUNCTION, LINE_OF_BUSINESS, FLOW_CODE, OWNERSHIP } from "@directv-digital/dds.utils";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import DtvButton, { IDtvButtonProps } from "@directv-digital/dds.ui.dtv-button";
// Generated by Copilot- Start - 2026-04-01 20:31:38
import { useGlobalConfigs } from "@directv-digital/dds.server.application-context";
import { filterAllChannelsNationalByDma, filterChannelsExcludedFromDisplayedPackages, filterSignatureExcludedLocalChannels, isLocalChannelExcludedFromSignature, signatureExclusionsForSurface } from "lib/service/channels/get-local-national-dma-eligible-channels";
// Generated by Copilot- End - 2026-04-01 20:31:38
import LocalRsnChnlProvider, { LocalRsnApiResponse, ProductType } from "@directv-digital/dds.apis.local-rsn-chnls";
import { buildLocalChannelObject } from "src/apis/channels-service-locals";
import ChannelLineUpProvider, {
  Channels,
  Packages,
  CHANNEL_LINEUP_VIEW,
  ChannelLineupApiData,
  KeyValuePair,
  ChannelFilter,
  Package,
  Channel,
  pluckArray,
  TAB_INDICATORS
} from "@directv-digital/dds.services.channel-lineup-provider";
import ChnlComparisonGrid from "./ChnlComparisonGrid";
import { normalizePkgName } from "./ChnlComparisonGrid/utils/pkgCalculations";
import NationalChnlTable from "./NationalChnlTable";
import ZipcodeModule, { ZipcodeModuleProps } from "src/components/video-lab/zipcode-module";
import SelectDropdown from "./SelectDropdown";
import { getArePackageQPsValid, isValidModalSrc } from './utils/utilityFunctions'
import PdfLinksErrorDisplay from "./PdfLinksErrorDisplay/pdfLinksErrorDisplay";
import ChannelSearch from "../ChannelSearch";
import { narrowRowsToSelectedIdentity } from "../ChannelSearch/utils/choice-key";
// [WAT-11766] The ONE reconciler, shared with the channel-lineup page. Long-term this belongs
// beside choice-key.ts rather than in ChlupStore; kept here for now so the in-review WAT-11789 PR
// rebases cleanly.
import { reconcileSearchSelections } from "src/components/ChnlListingPageV2/ChlupStore";

export interface ChnlCmpToolProps {
  searchChannleList?: any;
  options?: ChnlCmpToolOptions;
  data?: ChannelLineupData;
}

export interface ChannelLineupData {
    analytics?: Analytics;
    channelData?: ChannelFeedData;
    theme?: any;
    searchObj?: any;
}

export interface ChannelFeed {
  /**
   * WAT-11362 — did the server source this logo from the OV catalog? Carried on the
   * channel so render sites never re-derive it from the url (they would be guessing
   * with a different rule than `pickChannelLogo` applied).
   */
  isOvLogo?: boolean;
  IsActive?: boolean;
  id?: string;
  callLetter?: string;
  logo?: string;
  label?: string;
  categoryId?: string;
  resolution?: string;
  packageIndicators?: any;
  isStreamInc?: boolean;
  isSatelliteInc?: boolean;
  isStreamAddonPromo?: boolean;
  isSatelliteAddonPromo?: boolean;
  chnlNum?: string;
  addOnPkgIndicators?: any;
  includedChnlPackages?: any;
  addonPromoChnlPackages?: any;
  mlb?: string;
  nba?: string;
  nhl?: string;
  wnba?: string;
  packageNames?: any;
}

export interface ChannelFeedData {
  channelCategories?: any[];
  channels?: ChannelFeed[];
  packageCategories?: any[];
  packages?: any[];
  // service?: Service;
  sortOptions?: any[];
  commonData?: {
    [key: string]: any;
  };
  localAndRegionalChannels?: any;
  legalInRSNTab?: any; // LegalProps;
  legalInNationalTab?: any; // LegalProps;
  legalBelowRSNTab?: any; // LegalProps;
  legalBelowRSNNew?: any;
  legalAnalytics?: any;
  contentText?: any;
  defaultSortOptions?: any;
  showCtaCI?: any;
  internetChnlsPDFLink?: IDtvButtonProps;
  satelliteChnlsPDFLink?: IDtvButtonProps;
  internetSpanishChnlsPDFLink?: IDtvButtonProps;
  satelliteSpanishChnlsPDFLink?: IDtvButtonProps;
  domName?: string;
  streamChnlsPDFLink?: IDtvButtonProps;
  streamSpanishChnlsPDFLink?: IDtvButtonProps;
  mySportsBetaPDFLink?: IDtvButtonProps;
  embeddedSvods?: any[];
  getOffersDTViEmbeddedIncludedSvods?: any;
  getOffersDTVsEmbeddedIncludedSvods?: any;
  isEmbeddedSvodsEnabled?: boolean;
  myFreeDTVChnls?: any; // Support for myFreeDTV from FFP
  genreNationalMapping?: any; // Support for genre nationals from global SB feed
  genreQueryParams?: string[]; // Genre queryparams from the CMS genre packs (via the page adapter)
}

export interface ChnlCmpToolOptions {
  breakpoint?: number;
  id?: string;
  index?: number;
  isChannelLineUpPage?: boolean,
  packageBgColor?: any;
  edspFlow?: boolean;
  modalView?: boolean;
  pageName?: string;
  tab?: string;
  lang?: string;
  category?: string;
  pkg?: string;
  cmprPkg?: string;
  /**
   * WAT-11878: 0-based column index for cmprPkg. Defaults to 0. Typed as
   * `number | string` because the CMS boundary is untyped and Storyblok may
   * deliver a numeric string; the initializer coerces via `Number(...)`.
   */
  cmprPkgPosition?: number | string;
  /** WAT-11878: packages to drop when cmprPkg is added. String, CSV, or array. */
  cmprPkgExcludePackages?: string | string[];
  inPage?: any;
  modalSrc?: string;
  showCTA?: string;
  channelsQP?: string;
  isLocal?: string;
  showPkg?: string;
  qp?: string;
  zipcodeModuleConfig?: ZipcodeModuleProps["ciConfig"];
  rsnTierLegalTextObj?: any;
  renderMode?: 'grid' | 'table'; // 'grid' for ChnlComparisonGrid (v2), 'table' for NationalChnlTable (ffp)
  useFFPMode?: boolean; // Enable FFP (channel-lineup-ffp) mode features
  zipcode?: string;
}

const { breakpoints } = DtvTheme;

const styles = {
  customPadding: {
    [breakpoints.down(breakpoints.values.sm)]: {
      paddingBottom: "0px"
    }
  },
  headline: {
    color: "#102641",
    [breakpoints.down(breakpoints.values.sm)]: {
      lineHeight: "1.1em",
      fontSize: "24px",
      paddingLeft: "20px"
    },
    [breakpoints.up(breakpoints.values.sm)]: {
      fontSize: "30px"
    },
    [breakpoints.up(breakpoints.values.md)]: {
      fontSize: "36px"
    },
    backgroundColor: "#ffffff",
    left: "0px",
    position: "sticky",
    top: 0,
    width: "100%",
    zIndex: 1002,
    minHeight: "48px"
  },

  nationalTitle: {
    fontFamily: "roboto-regular",
    fontWeight: 400,
    paddingBottom: DtvTheme.spacing(0),
    paddingTop: "10px",
    marginBottom: "0px",
    fontSize: "20px !important",
    [breakpoints.between(breakpoints.values.sm, breakpoints.values.md)]: {
      paddingBottom: DtvTheme.spacing(0)
    },
    [breakpoints.down(breakpoints.values.sm)]: {
      color: "#102641",
      paddingBottom: "0px",
      paddingTop: "0",
      fontSize: "16px !important"
    }
  },
  filterSection: {
    display: "block",
    fontFamily: "roboto-regular",
    marginBottom: "20px",
    marginTop: "12px",
    [breakpoints.down(breakpoints.values.sm)]: {
      marginTop: "16px"
    }
  },
  filterSectionFlexDiv: {
    alignItems: "center",
    display: "inline-flex", // "flex",
    flex: "0 1 auto",
    width: "100%",
    justifyContent: "end",
    [breakpoints.down(breakpoints.values.sm)]: {
      alignItems: "unset",
      display: "inline-block",
      flex: "none",
      width: "unset",
      justifyContent: "unset"
    },
    [breakpoints.between(breakpoints.values.sm, breakpoints.values.md)]: {
      alignItems: "center"
    }
  },
  sortDropdown: {
    position: "relative",
    [breakpoints.down(breakpoints.values.sm)]: {
      marginLeft: "0px !important",
      marginTop: "16px",
      paddingLeft: "0px !important"
    },
    [breakpoints.between(breakpoints.values.sm, breakpoints.values.md)]: {
      paddingRight: "0px !important",
      backgroundColor: colorPalette.neutral_white.main,
      minWidth: ""
    },
    [breakpoints.up(breakpoints.values.md)]: {
      maxWidth: "335px",
      width: "100%"
    }
  },
  pdfLinkDefaultStyles: {
    display: "block !important",
    textAlign: "left",
    fontSize: {
      sm: "14px",
      md: "16px"
    },
    color: "#2E70BD",
    minWidth: 'max-content',
    padding: "8px 4px 6px 4px",
    height: "auto",
    "&:focus": {
      border: "none",
      outline: "1px solid #3885E1",
      borderRadius: '16px',
      color: "#2E70BD"
    }
  }
};

const SideArrow = () => (
  <svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg" style= {{
 position: "relative",
top: "2px"
}}>
    <path d="M5.98881 13.5467C5.71207 13.5467 5.43082 13.4415 5.21759 13.2398C4.79568 12.8277 4.79568 12.1614 5.21759 11.7536L9.11005 8.00099L5.20851 4.23956C4.78206 3.82746 4.78206 3.1655 5.20851 2.7534C5.63496 2.34131 6.32001 2.34131 6.74645 2.7534L11.1108 6.962C11.392 7.2338 11.5553 7.61081 11.5553 8.00537C11.5553 8.39554 11.3966 8.77694 11.1153 9.04875L6.76008 13.2486C6.54685 13.4546 6.27009 13.5554 5.99335 13.5554" fill="#2E70BD"/>
  </svg>
);

// WAT-11878: whole-catalog lookups (Spanish-tier cmprPkg / ?pkg=, cmprPkg
// validation) intentionally bypass clupConfig.maxPackagesToDisplay so any
// tier can be resolved. 100 is a soft ceiling; the modal caps the rendered
// column count downstream via maxPackagesToDisplay.
const ALL_PACKAGES_CAP = 100;

const clupConfig = {
  maxPackagesToDisplay: 6,
  baseChnlFilter: {
    addOnPackages: true,
    hd: false,
    promoPackages: true
  } as ChannelFilter
};

const getSortForViewMode = mode => {
  switch (mode) {
    case CHANNEL_LINEUP_VIEW.HD:
      return Channels.sortByHd;
    case CHANNEL_LINEUP_VIEW.CATEGORY:
      return Channels.sortAlphabetically;
    case CHANNEL_LINEUP_VIEW.CHANNELS:
      return Channels.sortAlphabetically;
    case CHANNEL_LINEUP_VIEW.CHANNELNUMBER:
      return Channels.sortByChannelNumber;
  }
};

export interface ChannelLineupState {
  rawFilteredChannelData: Channels;
  channelDataObjects: ChannelLineupApiData;
  masterChannels: Channels;
  displayChannels: Channel[];
  masterPackages: Packages;
  channelFilter: ChannelFilter;
  channelCategories: KeyValuePair[];
  packages: Package[];
  packageCategories: KeyValuePair[];
  filterPackageKey: string;
  viewMode: CHANNEL_LINEUP_VIEW;
}

export const channelLineupStateInitializer = ({ channelData, channelsQP, pkg, cmprPkg, cmprPkgPosition, cmprPkgExcludePackages, edspFlow, isMySportsBetaView,  defaultCategoryValue, msbNationalRollOut }) => {
  // isStreamInc / isStreamAddonPromo are computed against the current language's
  // packages only, so a channel included solely in a package of another language
  // (EWTN Español: Óptimo Más only, no English add-ons) has both false. Keep any
  // channel included in an internet package present in the feed's package list (any
  // language). NOTE: the full feed list, not the rendered columns — the column
  // pruning/cmprPkg steps run later. The set holds internet (ATT_TV_) ids only.
  const feedStreamPkgIds = new Set((channelData?.packages ?? []).map(p => p?.packageId)
    .filter(id => typeof id === "string" && id.startsWith("ATT_TV_")));
  const isInFeedStreamPackage = chnl => (chnl?.includedChnlPackages ?? []).some(id => feedStreamPkgIds.has(id));
  const filteredChannelData: Channels = channelData?.channels?.filter?.(chnl => (channelsQP?.toUpperCase() === "SATELLITE" ?
    // FAST-flagged channels aren't included/add-on via satellite (isSatelliteInc/
    // isSatelliteAddonPromo both false for them) but still belong in this view —
    // they're routed into the MyFreeDIRECTV section instead.
    (chnl?.isSatelliteInc || chnl?.isSatelliteAddonPromo || chnl?.isMyFreeDTV) :
    chnl?.isStreamInc || chnl?.isStreamAddonPromo || isInFeedStreamPackage(chnl)));

  let  filteredPackage = channelData?.packages;
  if(isMySportsBetaView && !msbNationalRollOut){
    if(channelData?.mySportsEligibleCookie === "true"){
      const mySportsPackage = channelData?.packages?.filter?.(pkgItem => pkgItem?.packageId === "ATT_TV_MYSPORTSBETA")?.[0]
      if(mySportsPackage){
        filteredPackage = channelData?.packages?.filter?.(pkgItem => pkgItem?.packageId !== "ATT_TV_PREMIER");
        if(pkg === "mysportsbeta"){
          const  _filteredChannelData = channelData?.channels?.filter?.(chnl => chnl?.isStreamInc && chnl?.includedChnlPackages?.includes?.("ATT_TV_MYSPORTSBETA"));
        }
      }
    } else {
      filteredPackage = channelData?.packages?.filter?.(pkgItem => pkgItem?.packageId !== "ATT_TV_MYSPORTSBETA");
    }
  }
  if(msbNationalRollOut){
    const mySportsPackage = channelData?.packages?.filter?.(pkgItem => pkgItem?.packageId === "ATT_TV_MYSPORTSBETA")?.[0]
    if(mySportsPackage){
      filteredPackage = channelData?.packages?.filter?.(pkgItem => pkgItem?.packageId !== "ATT_TV_PREMIER");
      if(pkg === "mysportsbeta"){
        const  _filteredChannelData = channelData?.channels?.filter?.(chnl => chnl?.isStreamInc && chnl?.includedChnlPackages?.includes?.("ATT_TV_MYSPORTSBETA"));
      }
    }
  }

  // Get initial channel data objects from CMS feed
  channelData.channels = filteredChannelData;
  channelData.packages = filteredPackage;
  const channelDataObjects: ChannelLineupApiData = ChannelLineUpProvider.getFromCmsFeed(channelData);
  // Initial channel filter state
  const filterPackageKey = channelDataObjects?.packageCategories?.[0]?.key || "";
  const initChnlFilter: ChannelFilter = {
    ...clupConfig.baseChnlFilter,
    selectedChannel: [],
    categoryId: filterPackageKey
  };

  // Order categories to match /channel-lineup (LOCAL → RSN → National Sports → …).
  // Structural LOCAL/RSN rows are keyed "LOCAL"/"RSN" (channels bucket by
  // chn.category === "LOCAL"/"RSN"). The old code led with RSN and tried to hoist a
  // "SPORTS"-keyed row that doesn't exist flag-ON (categories arrive keyed by their
  // prose label, e.g. "National Sports"), so the hoist no-opped and RSN/LOCAL ended up
  // swapped vs the lineup. Inject LOCAL then RSN; National Sports + content follow in
  // the adapter's already-lineup-aligned order.
  const initChnlCategories: KeyValuePair[] = channelDataObjects.channelCategories = [
    {
      key: "LOCAL",
      value: "Local channels"
    },
    {
      key: "RSN",
      value: "Regional sports"
    },
    ...channelDataObjects.channelCategories.filter(ctg => ctg.key !== "LOCAL" && ctg.key !== "RSN")
  ];

  // get list of actively used packages
  let masterPackages = new Packages();
  masterPackages = channelDataObjects.packages;
  let initPackages: Package[] = masterPackages.filterByCategory(
    filterPackageKey,
    clupConfig?.maxPackagesToDisplay,
    !!edspFlow
  );

  // WAT-11878: if cmprPkg names a package outside the base language set
  // (Spanish tier / genre pack), inject it at the CMS-configured position
  // and drop CMS-configured packages. AC-10 defaults: Optimo Más → position 0
  // with Premier dropped; other injected packages → position 0, no drop.
  const findByShortName = (shortName: string, catalog: Package[]): Package | undefined => {
    const target = normalizePkgName(shortName);
    if (!target) {
      return undefined;
    }
    return catalog.find(candidate => normalizePkgName(candidate?.name) === target);
  };
  // WAT-11878: when cmprPkg injects a package whose `.category` (the DDS
  // Package's language slot — the raw feed's `categoryId` mapped by
  // `getFromCmsFeed`) differs from the base view's `filterPackageKey`
  // (e.g. Óptimo Más's SPANISH into an ENGLISH view), the displayChannels
  // filter below must include channels from THAT language slot too —
  // otherwise Spanish-only channels the Óptimo Más column covers never get
  // a row. `injectedPkgCategoryId` here is the DDS language KEY
  // ("ENGLISH"/"SPANISH"), not the ChannelFilter's own `categoryId` field
  // (which is a different concept, though named the same in that type).
  let injectedPkgCategoryId: string | undefined;
  if (cmprPkg) {
    // Dedupe key: DDS `Package` exposes `.id`; the `.packageId` fallback is
    // defensive for pre-DDS or raw feed entries that reach this path without
    // going through the DDS mapper.
    const packageKey = (p: any): string => p?.id ?? p?.packageId ?? p?.name ?? '';
    const allPackagesFromCatalog = masterPackages?.filterByCategory?.('ALL', ALL_PACKAGES_CAP, !!edspFlow);
    if (!allPackagesFromCatalog) {
      // Signal a degraded catalog — cmprPkg injection will no-op instead of
      // silently. Warn (not error) so a stale mock in tests doesn't fail.
      console.warn(
        '[ChannelCompareToolV3] masterPackages.filterByCategory unavailable; cmprPkg injection skipped'
      );
    }
    const allPackages: Package[] = allPackagesFromCatalog ?? [];
    const cmprPkgObj = findByShortName(cmprPkg, allPackages);
    const alreadyInInit = cmprPkgObj && initPackages.some(p => packageKey(p) === packageKey(cmprPkgObj));
    if (cmprPkgObj && !alreadyInInit) {
      const isOptimoMas = normalizePkgName(cmprPkgObj?.name) === 'optimomas';
      // Excludes: string / CSV / array. Filter empties first so an empty string
      // or lone comma is treated as unauthored (does not cancel the AC-10 default).
      const rawExcludes = Array.isArray(cmprPkgExcludePackages) ?
        cmprPkgExcludePackages :
        typeof cmprPkgExcludePackages === 'string' ?
          cmprPkgExcludePackages.split(',') :
          [];
      const cmsExcludeKeys = rawExcludes.map(normalizePkgName).filter(Boolean);
      const excludeKeys = cmsExcludeKeys.length > 0 ?
        cmsExcludeKeys :
        (isOptimoMas ? ['premier'] : []);
      const baseFiltered = excludeKeys.length > 0 ?
        initPackages.filter(p => !excludeKeys.includes(normalizePkgName(p?.name))) :
        initPackages;
      // Trim the base to cap-1 BEFORE splicing so the injected column always
      // survives — a large / misconfigured cmprPkgPosition can't push the
      // injected package past the cap and get sliced off. Position: 0 = first
      // column, clamped for negatives / NaN / out-of-range.
      const cap = clupConfig?.maxPackagesToDisplay ?? Number.MAX_SAFE_INTEGER;
      const baseCap = Math.max(0, cap - 1);
      const trimmedBase = baseFiltered.slice(0, baseCap);
      const rawPosition = Number(cmprPkgPosition);
      const requestedPosition = Number.isFinite(rawPosition) && rawPosition >= 0 ?
        Math.floor(rawPosition) :
        0;
      const insertAt = Math.min(requestedPosition, trimmedBase.length);
      initPackages = [
        ...trimmedBase.slice(0, insertAt),
        cmprPkgObj,
        ...trimmedBase.slice(insertAt)
      ];
      // Cross-language injection surfaces here — the injected pkg's own
      // language (e.g. "SPANISH") is what expands the channel-row filter
      // below. DDS `Package` instances expose the language on `.category`
      // (the raw feed's `categoryId` is mapped by `getFromCmsFeed`).
      const injectedCategory = (cmprPkgObj as any)?.category;
      if (injectedCategory && injectedCategory !== filterPackageKey) {
        injectedPkgCategoryId = injectedCategory;
      }
    }
  }

  const initViewMode: CHANNEL_LINEUP_VIEW =
    defaultCategoryValue === "category" ? CHANNEL_LINEUP_VIEW.CATEGORY : CHANNEL_LINEUP_VIEW.CHANNELS;

  // WAT-11878: `channels.filter(chnlFilter)` is keyed on a SINGLE `categoryId`
  // (the ChannelFilter's own key, = the DDS Package language slot in this
  // tool), so a cmprPkg injection from a different language group would drop
  // that group's channel rows. Union base + injected-language rows by id, and
  // run the sort ONCE over the merged set so injected-language rows land in
  // their correct sorted position (not appended at the tail).
  const sortFn = getSortForViewMode(initViewMode);
  const initDisplayChannels = (() => {
    const baseChannels = channelDataObjects.channels.filter(initChnlFilter, sortFn);
    if (!injectedPkgCategoryId) {
      return baseChannels;
    }
    const extendedFilter: ChannelFilter = {
      ...initChnlFilter,
      categoryId: injectedPkgCategoryId
    };
    const extraChannels = channelDataObjects.channels.filter(extendedFilter, sortFn);
    if (!extraChannels?.length) {
      return baseChannels;
    }
    // Skip null/undefined/NaN ids from the seen set — Set uses SameValueZero,
    // so a base channel with `id === NaN` would swallow every extra channel
    // that also has a missing id (false-positive duplicates).
    const isConcreteId = (v: any): boolean => v !== null && v !== undefined && !(typeof v === 'number' && Number.isNaN(v));
    const seenIds = new Set(baseChannels.map((c: any) => c?.id).filter(isConcreteId));
    const uniqueExtras = extraChannels.filter((c: any) => !isConcreteId(c?.id) || !seenIds.has(c?.id));
    if (!uniqueExtras.length) {
      return baseChannels;
    }
    // Sort the merged set in a single pass — mirror what filter(…, sortFn)
    // would have done if the DDS API accepted a multi-category filter.
    return [...baseChannels, ...uniqueExtras].sort(sortFn);
  })();

  const clupState: ChannelLineupState = {
    /** raw data passed through initial channelsQP filter. Only used for later concatenating new list of master Channels */
    rawFilteredChannelData: filteredChannelData,
    /** Never updated, used for static reference attributes */
    channelDataObjects,
    /** master list of channels, passed through getFromCmsFeed*/
    masterChannels: channelDataObjects.channels,
    /** list of displayed channels, passed through getFromCmsFeed, filtered, and sorted */
    displayChannels: initDisplayChannels,
    /** master list of packages, passed through getFromCmsFeed*/
    masterPackages: channelDataObjects.packages,
    /** displayed packages, filtered from masterPackages by filterPackageKey and
     * edspFlow. WAT-11878: when `?cmprPkg=<name>` names a package outside the
     * base language set, `initPackages` also has that package spliced in at
     * `cmprPkgPosition` (CMS) or position 0 (default), with CMS `cmprPkgExcludePackages`
     * removed (Optimo Más → Premier dropped by default per AC-10). */
    packages: initPackages,
    channelFilter: initChnlFilter,
    channelCategories: initChnlCategories,
    packageCategories: channelDataObjects?.packageCategories,
    filterPackageKey,
    viewMode: initViewMode
  };
  return clupState;
};

export function channelLineupStateReducer(state, action) {
  switch (action.type) {
    case 'SET_MASTER_CHANNELS':
      return {
        ...state,
        masterChannels: action.payload
      };
    case 'SET_DISPLAY_CHANNELS':
      return {
        ...state,
        channels: action.payload
      };
    case 'CONCAT_LOCAL_RSN_CHANNELS':
      return {
        ...state,
        localRsnChannels: action.payload
      };
    case 'SET_CHANNEL_FILTER':
      return {
        ...state,
        channelFilter: action.payload
      };
    case 'SET_VIEW_MODE':
      return {
        ...state,
        viewMode: action.payload
      };
    case 'SET_CHANNEL_CATEGORIES':
      return {
        ...state,
        channelCategories: action.payload
      };
    case 'SET_PACKAGES':
      return {
        ...state,
        packages: action.payload
      };
    case 'SET_MASTER_PACKAGES':
      return {
        ...state,
        masterPackages: action.payload
      };
    case 'SET_PACKAGE_CATEGORIES':
      return {
        ...state,
        packages: action.payload
      };
    default:
      return state;
  }
}

// eslint-disable-next-line complexity
export function ChannelCompareToolV3(props: ChnlCmpToolProps) {
  try {
    const { data, options } = props;
    const { channelData, theme, searchObj } = data;
    const channelDataWithSvods = channelData as ChannelFeedData;
    const {
      id,
      isChannelLineUpPage = false,
      isLocal,
      category,
      cmprPkg,
      pkg,
      pageName,
      edspFlow,
      showPkg,
      zipcodeModuleConfig
    } = options;
    const modalSrc = options?.modalSrc?.toUpperCase() || "";
    const channelsQP = options?.channelsQP?.toUpperCase() || "";
    const useStream = !channelsQP || (channelsQP === "STREAM" || channelsQP === "INTERNET");
    const useSatellite = channelsQP === "SATELLITE";
    const lang = options?.lang?.toUpperCase() || "";
    const streamChnlsPDFLink = channelData?.streamChnlsPDFLink;
    const mySportsBetaPDFLink = channelData?.mySportsBetaPDFLink;
    const streamSpanishChnlsPDFLink = channelData?.streamSpanishChnlsPDFLink;
    const domName: any = channelData && channelData.domName ? channelData.domName : '';
    const defaultCategoryValue = category?.toLowerCase() || channelData?.defaultSortOptions?.toLowerCase();
    let isMySportsBetaView = cmprPkg === 'mysportsbeta' || pkg === 'mysportsbeta' || showPkg === 'mysportsbeta'

    // Generated by Copilot- Start - 2026-04-01 20:31:38
    const globalConfigs = useGlobalConfigs();
    const msbNationalRollOut = globalConfigs?.msbNationalRollOut;
    // [WAT-11768] Stream-only rule — the SATELLITE view of this tool must not hide a local.
    const signatureExcludedLocalChannels = signatureExclusionsForSurface(globalConfigs?.signatureExcludedLocalChannels, useSatellite);
    // Generated by Copilot- End - 2026-04-01 20:31:38
    isMySportsBetaView = msbNationalRollOut ? true : isMySportsBetaView;
    const [isComputing, setIsComputing] = useState(true);
    const [toolState, dispatchToolState] = useReducer(
      channelLineupStateReducer,
      {
        channelData,
        channelsQP,
        edspFlow: !!edspFlow,
        isMySportsBetaView,
        pkg,
        // WAT-11878: cmprPkg + optional CMS knobs (position, excludes).
        // Defaults handled inside the initializer.
        cmprPkg,
        cmprPkgPosition: options?.cmprPkgPosition,
        cmprPkgExcludePackages: options?.cmprPkgExcludePackages,
        defaultCategoryValue,
        msbNationalRollOut
      },
      channelLineupStateInitializer
    );
    const [rsnChnlsObj, setRsnChnlsObj] = useState({});

    const {
      localAndRegionalChannels,
      commonData,
      legalInNationalTab,
      // legalInRSNTab,
      contentText,
      // service,
      sortOptions,
      internetChnlsPDFLink,
      internetSpanishChnlsPDFLink,
      satelliteChnlsPDFLink,
      satelliteSpanishChnlsPDFLink
    }: ChannelLineupApiData = toolState.channelDataObjects;

    const [apiError, setApiError] = useState<boolean>(false);
    const [isApiLoading, setIsApiLoading] = useState<boolean>(false);
    const [showChnlGrid, setShowChnlGrid] = useState(options?.useFFPMode ? "display" : "");
    const [selectedValue, setSelectedValue] = useState(defaultCategoryValue === "category" ? 2 : 0);
    const [customPkgIndex, setCustomPkgIndex] = useState(() => (options?.pkg ? 0 : null));
    const [customPkg, setCustomPkg] = useState([]);
    const [localMySportsGenreChannels, setLocalMySportsGenreChannels] = React.useState([])
    const [filteredEmbeddedSvods, setFilteredEmbeddedSvods] = useState(() => channelDataWithSvods?.embeddedSvods || []);
    const embeddedSvodChoicesInitializedRef = useRef(false);

    // FFP Mode Support (from channel-lineup-ffp)
    const renderMode = options?.renderMode || (options?.useFFPMode ? 'table' : 'grid');
    const [activeTab, _setActiveTab] = React.useState(
      options?.tab === "rsn" ? TAB_INDICATORS.RSN : TAB_INDICATORS.NATIONAL
    );
    const myFreeDTVChnls = channelData?.myFreeDTVChnls;
    const myFreeDTVPdf = props?.data?.channelData?.myFreeDTVChnls?.pdfLink;

    const isModalSrcValid = modalSrc !== "" ? isValidModalSrc(modalSrc) : true;
    const isChannelsQPValid = !(channelsQP &&
    (channelsQP !== "INTERNET" && channelsQP !== "SATELLITE"));
    const isLangQPValid = !(lang &&
    (lang !== "ENGLISH" && lang !== "SPANISH"));
    const isErrorScenario = (lang && lang === "SPANISH") ? "spanish" : "english";
    // WAT-11878: validate against the WHOLE catalog. Was English-only, which
    // hard-failed every Spanish-tier cmprPkg (optimomas/maslatino/etc).
    const allPackagesForValidation = React.useMemo(
      () => toolState.masterPackages?.filterByCategory?.('ALL', ALL_PACKAGES_CAP, !!edspFlow) ?? toolState.packages,
      [toolState.masterPackages, edspFlow, toolState.packages]
    );
    const { isPkgQPValid, isCmprPkgQPValid } =
    getArePackageQPsValid(pkg, cmprPkg, allPackagesForValidation);

    const [showyMySportsbetaInEligibleMessage, _setMySportsbetaInEligibleMessage] = useState(false);

    /** From Chnl Listing Page MultiSearch */
    useEffect(() => {
      if(props?.searchChannleList) {
        dispatchToolState({
          type: 'SET_CHANNEL_FILTER',
          payload: {
            ...toolState.channelFilter,
            selectedChannel: props?.searchChannleList
          }
        });
      }
    }, [props?.searchChannleList]);

    useEffect(() => {
      embeddedSvodChoicesInitializedRef.current = false;
      setFilteredEmbeddedSvods([]);
    }, [channelsQP]);

    const handleEmbeddedSvodsFiltered = useCallback((svods = []) => {
      if (embeddedSvodChoicesInitializedRef.current) {
        return;
      }
      setFilteredEmbeddedSvods(Array.isArray(svods) ? svods : []);
      embeddedSvodChoicesInitializedRef.current = true;
    }, [setFilteredEmbeddedSvods]);

    const updateSvodFilterData = useMemo(() => {
      const filterdChannels = toolState.channelFilter?.selectedChannel;
      const embeddedSvodsData = channelDataWithSvods?.embeddedSvods;
      if (Array.isArray(embeddedSvodsData) && embeddedSvodsData.length > 0 && Array.isArray(filterdChannels)) {
        if (filterdChannels?.length > 0) {
          const filterNames = new Set(filterdChannels.map((ch: any) => (ch.name || ch.value)?.toLowerCase()));
          const embeddedSvodsFiltered = embeddedSvodsData.filter((svod: any) => filterNames.has((svod.name || svod.value)?.toLowerCase()));
          return embeddedSvodsFiltered;
        }
      }
      return embeddedSvodsData;
    }, [toolState.channelFilter, toolState.viewMode, channelDataWithSvods?.embeddedSvods]);

    /** Change language selection.
     * `skipPackagesDispatch` (WAT-11878): true when the caller wants the
     * language-tab + categoryId update but NOT the SET_PACKAGES dispatch —
     * used from the ?cmprPkg= mount path where the initializer already
     * resolved the CMS-aware column set (Óptimo Más injected + Premier
     * dropped) and re-running filterByCategory(lang, …) would overwrite it.
     */
    const changeLanguageSelection = (val, flag, skipPackagesDispatch = false) => {
      const selectedPkg = val.target.getAttribute("data-value");
      if (!skipPackagesDispatch) {
        const newPkgs = toolState.masterPackages.filterByCategory(
          selectedPkg,
          clupConfig.maxPackagesToDisplay,
          !!edspFlow
        );
        dispatchToolState({
          type: "SET_PACKAGES",
          payload: newPkgs
        });
      }
      dispatchToolState({
        type: "SET_CHANNEL_FILTER",
        payload: {
          ...toolState.channelFilter,
          categoryId: selectedPkg
        }
      });

      if (flag) {
        setCustomPkg([]);
      }
      fireLinkClickEvent({
        linkDestination: "",
        linkName: `${selectedPkg}`,
        linkPosition: LINK_POSITION_BODY
      });
    };

    /** - Update selected value for dropdown and sort channels */
    const changeViewMode = option => {
      const value = option?.key;
      const mode = parseInt(value, 10);
      setSelectedValue(mode);
      dispatchToolState({
        type: "SET_VIEW_MODE",
        payload: value
      });
      const optionValue = `${sortOptions?.find(option => option?.key === value)?.value}`;
      fireLinkClickEvent({
        linkDestination: "",
        linkName: `Display_${optionValue}`,
        linkPosition: LINK_POSITION_BODY
      });

      fireCustomLinkClickGA({
        event: "trackEvent",
        event_name: "custom_link_click",
        linkName: `sortby_${optionValue?.toLowerCase()}`,
        linkPosition: 'modal_channel lineup',
        linkDestination: window?.location?.href
      });
    };

    /** Same as change view mode, for grid radio buttons */
    const changeViewModeFromNationalTable = val => {
      const mode = parseInt(val, 10);
      setSelectedValue(mode);
      dispatchToolState({
        type: "SET_CHANNEL_FILTER",
        payload: {
          ...toolState.channelFilter,
          ...clupConfig.baseChnlFilter
        }
      });
      dispatchToolState({
        type: "SET_VIEW_MODE",
        payload: val
      });

      fireLinkClickEvent({
        linkDestination: "",
        linkName: `Display_${sortOptions?.find(option => option?.key === val)?.value}`,
        linkPosition: LINK_POSITION_BODY
      });
    }

    /**
   * 1. send GA product impression for each displayed package
   * 2. Fire page load event
   * 3. Change the View mode/packages based on options.lang and default category
   */
    useEffect(() => {
      if (
        options?.id?.toLowerCase().indexOf("channellineup") !== -1 &&
      channelData?.packages?.length > 0
      ) {
        const cardDataGA = channelData?.packages
          ?.filter(pkg => Object.prototype.hasOwnProperty.call(pkg, "googleAnalytics"))
          ?.map((pkg, index) => ({
            id: pkg.googleAnalytics.id,
            position: index + 1
          }));
        if (cardDataGA?.length > 0) {
          fireGa4ProductImpression(cardDataGA);
        }
      }

      firePageLoadEvent({
        "page.category.applicationName": APPLICATION_NAME,
        "page.category.pageFunction": PAGE_FUNCTION,
        "page.category.pageOwnership": OWNERSHIP,
        "page.pageInfo.flowCode": FLOW_CODE,
        "page.pageInfo.lineOfBusiness": LINE_OF_BUSINESS,
        "page.location.url": window.location.pathname
      });

      const defaultCategory = category || channelData?.defaultSortOptions;
      const sortCtgry = defaultCategory ? sortOptions.find(ctg => ctg?.value?.toLowerCase?.() === String(defaultCategory).toLowerCase()) : undefined;
      const langCtg = options?.lang && toolState.packageCategories.find(pkgCtg => pkgCtg?.value?.toLowerCase() === lang?.toLowerCase());

      if (sortCtgry) {
        changeViewMode({ target: { value: sortCtgry?.key } });
      }

      // WAT-11878: `setIsComputing(false)` must run on every branch reachable
      // when `?pkg=` OR `?lang=` is set — otherwise `?pkg=X&cmprPkg=Y` falls
      // through all three branches and hangs the grid at the loading gate
      // (isReadyForTable stays false). When cmprPkg is set, the initializer
      // already resolved the CMS-aware column set (Óptimo Más injected,
      // Premier dropped); skip the SET_PACKAGES dispatch inside
      // changeLanguageSelection AND skip the pkg-lookup SET_PACKAGES so the
      // injected column survives the mount effect.
      if (langCtg && options?.pkg) {
        changeLanguageSelection(
          {
            target: {
              getAttribute: () => langCtg.key
            }
          },
          false,
          !!options?.cmprPkg
        );
        if (!options?.cmprPkg) {
          const packagesX = toolState.masterPackages.filterByCategory(
            langCtg.key,
            clupConfig.maxPackagesToDisplay,
            !!edspFlow
          );
          const pkgKey = normalizePkgName(options?.pkg);
          const pkg = options?.pkg && packagesX.filter(pkgX => normalizePkgName(pkgX?.name) === pkgKey);
          dispatchToolState({
            type: "SET_PACKAGES",
            payload: pkg
          });
          setCustomPkg(pkg);
          setCustomPkgIndex(0);
        }
        setIsComputing(false);
      } else if (options?.lang) {
        changeLanguageSelection(
          {
            target: {
              getAttribute: () => langCtg?.key || ''
            }
          },
          false,
          !!options?.cmprPkg
        );
        setIsComputing(false);
      } else if (options?.pkg) {
        // Search across all categories (fixes blank ?pkg=optimomas w/o
        // lang=spanish). filterByCategory('ALL', …) returns the whole catalog
        // per DDS ChannelClasses; the `.packages` fallback covers a
        // degraded catalog where filterByCategory returns undefined.
        const searchablePkgs = toolState.masterPackages?.filterByCategory?.(
          'ALL',
          ALL_PACKAGES_CAP,
          !!edspFlow
        ) ?? toolState.packages;
        const pkgKey = normalizePkgName(pkg);
        const pkgsArray = searchablePkgs.filter(x => {
          const nameKey = normalizePkgName(x?.name);
          if (nameKey === 'mysportsbeta') {
            return nameKey !== pkgKey;
          }
          return nameKey === pkgKey;
        });
        if (pkgsArray?.length > 0) {
          if (options?.cmprPkg) {
            // WAT-11878: cmprPkg wins for the column set, but preserve the
            // pre-WAT-11878 semantics of `?pkg=` by still resolving pkg into
            // customPkg — so the highlighted column tracks pkg within the
            // CMS-aware set the initializer built. If pkg matches a column
            // already in that set, point customPkgIndex at it; otherwise
            // fall back to index 0.
            const currentPkgs = toolState.packages ?? [];
            const foundIndex = currentPkgs.findIndex(p => normalizePkgName(p?.name) === pkgKey);
            setCustomPkg(pkgsArray);
            setCustomPkgIndex(foundIndex !== -1 ? foundIndex : 0);
          } else {
            const tempLang = toolState.packageCategories.find(
              pCtg => pCtg?.key?.toLowerCase() === pkgsArray?.[0]?.category?.toLowerCase()
            );
            const newChnlFilter: ChannelFilter = {
              ...toolState.channelFilter,
              categoryId: tempLang?.key ?? toolState.channelFilter.categoryId
            };
            dispatchToolState({
              type: "SET_PACKAGES",
              payload: pkgsArray
            });
            dispatchToolState({
              type: "SET_CHANNEL_FILTER",
              payload: newChnlFilter
            });
            setCustomPkg(pkgsArray);
            setCustomPkgIndex(0);
          }
        }
        setIsComputing(false);
      }
    }, []);

    // Generated by Copilot- Start - 2026-04-13 14:06:05
    const urlZipcode = (options?.zipcode && /^\d{5}$/.test(options.zipcode)) ? options.zipcode : null;
    // Generated by Copilot- End - 2026-04-13 14:06:05

    const setLoadingLocalRsnsChannels = React.useCallback(() => {
      setIsApiLoading(true);
      setShowChnlGrid("hide");
    }, []);

    /** Handle Local/Rsn call on load and county search */
    // Zipcode module handles all cookie/context updating logic
    const addLocalRsnChnls = React.useCallback((channels: LocalRsnApiResponse | null, pDma?: string, pNielsenDma?: string) => {
      const { localChannels = [], rsnChannels = [] } = channels;

      const tempRsnObj = {}
      rsnChannels?.forEach(ch => {
        tempRsnObj[ch?.callSign] = ch;
      })
      setRsnChnlsObj(tempRsnObj);

      const localChannelsData = localChannels?.map?.((chnl: any) => buildLocalChannelObject(
        chnl, useSatellite ? ProductType.SATELLITE : ProductType.STREAM
      ));
      // Keep an RSN channel only when its packageNames overlap an available package.
      // Match on the TIER name — strip the platform prefix (ATT_TV_/DTV_TV_/DTV_) and
      // normalize space vs underscore — so satellite ids "DTV_TV_CHOICE"/"DTV_MAS_ULTRA"
      // match the feed's bare "CHOICE"/"MAS ULTRA". The old strict split("DTV_")[1]
      // compared "TV_CHOICE"/"MAS_ULTRA" and dropped EVERY satellite RSN channel.
      // normTier + the tier list are hoisted out of the per-channel filter (computed once).
      const rsnNormTier = (s: any) =>
        String(s ?? "").toLowerCase().replace(/^(att_tv_|dtv_tv_|dtv_)/, "").replace(/[\s_]+/g, " ").trim();
      const rsnPkgTiers = (toolState?.packages ?? []).map((pkg: any) => rsnNormTier(pkg?.id));
      const rsnChannelsData =  rsnChannels?.map?.((chnl: any) => LocalRsnChnlProvider?.buildChannelObject(
        {
          ...chnl,
          logo: chnl?.logo?.split('rsn-channels/')?.[1]
        },
        'rsn', useSatellite ? ProductType.SATELLITE : ProductType.STREAM
      ))?.filter(channel => (channel?.packageNames || []).some((cp: any) => rsnPkgTiers.includes(rsnNormTier(cp))));

      // Generated by Copilot- Start - 2026-04-23 09:00:00
      // Two-pass exclusion:
      //   (1) Drop locals fully excluded for this DMA (blanket case — no caller package needed)
      //   (2) For survivors, prune narrowly-excluded signature pkgs from each channel's packageNames
      //       so the compare grid hides them column-by-column.
      const dmaForExclusion = pDma || pNielsenDma || "";
      const filteredLocalChannels = filterSignatureExcludedLocalChannels(
        localChannelsData || [], dmaForExclusion, signatureExcludedLocalChannels
      );

      const prunePackageId = (pkgId: string): string => pkgId.split("ATT_TV_")[1]?.toLowerCase?.() || pkgId.split("DTV_")[1]?.toLowerCase?.() || pkgId.toLowerCase();

      const prunedLocalChannels = filteredLocalChannels.map(channel => {
        const network = channel?.network || channel?.name || channel?.channelNameLabel || channel?.label;
        if (!network || !Array.isArray(channel?.packageNames)) {
          return channel;
        }

        const excludedShortNames = (toolState?.packages || [])
          .filter(pkg => pkg?.id && isLocalChannelExcludedFromSignature(network, dmaForExclusion, signatureExcludedLocalChannels, pkg.id, channel))
          .map(pkg => prunePackageId(pkg.id))
          .filter(Boolean);

        if (excludedShortNames.length === 0) {
          return channel;
        }
        return {
          ...channel,
          packageNames: channel.packageNames.filter(
            (pn: string) => !excludedShortNames.includes(pn?.toLowerCase?.())
          )
        };
      }).filter(channel => {
      // Drop locals with no remaining packages after narrow pruning — otherwise
      // they'd render as empty rows in the compare grid.
        if (!Array.isArray(channel?.packageNames)) {
          return true;
        }
        return channel.packageNames.length > 0;
      });
      // Generated by Copilot- End - 2026-04-23 09:00:00

      const respData = isLocal === "false" ?
        [...(rsnChannelsData || [])] :
        [...prunedLocalChannels, ...(rsnChannelsData || [])]

      const mySportsEligibleDma = globalConfigs?.mySportsEligibleDma?.[0];
      const tempMySportsEligibleDmaLocalChannels =
      (mySportsEligibleDma?.[pNielsenDma]?.[0]?.localChannels || []) ||
      (mySportsEligibleDma?.[pDma]?.[0]?.localChannels || [])
      setLocalMySportsGenreChannels(tempMySportsEligibleDmaLocalChannels);

      // Generated by Copilot- Start - 2026-03-18 10:15:00
      const genreNationalMapping = channelData?.genreNationalMapping || [];
      const channelAliases = genreNationalMapping
        .filter(genreNational => localChannelsData?.some(localChannel => localChannel?.name === genreNational?.channel ||
          localChannel?.network === genreNational?.channel ||
          localChannel?.label === genreNational?.channel))
        .flatMap(genreNational => genreNational?.channelAliases || []);

      // In an Óptimo-Más national market the beam locals API sends the national Spanish feed as an
      // alias-named LOCAL (e.g. "Telemundo East") with no package data, which the grid then defaults
      // into all 4 English signature tiers. filterAllChannelsNationalByDma keeps a real local affiliate
      // (named by the base channel) but drops the alias-named pseudo-local, leaving the correct
      // Spanish-only national from the master feed as the only Telemundo — matching the agent tool
      // (WAT-11284) and channel-lineup. Genre queryparams come from the CMS genre packs via the page
      // adapter — the same source the agent package-compare tool derives from (WAT-11284). [WAT-11301]
      const genreQueryParams = channelData?.genreQueryParams || [];
      const dedupedFeedChannels = toolState.rawFilteredChannelData.concat(respData)
        .filter(c => {
          const isLocalOrRsn = c?.categoryId === "LOCAL" || c?.categoryId === "RSN";
          const result = isLocalOrRsn ? true : !channelAliases.includes(c?.label);
          return result;
        });

      // Key the national gate off dmaForExclusion (pDma || pNielsenDma) — the SAME DMA the
      // displayed-package exclusion below uses, and the precedence isNationalForGenre /
      // handleLocalRsnChannelFilter already use for genre-national eligibility. Keying it off a
      // different precedence would rename against one DMA and exclude against another. [WAT-11301]
      const nationalFilteredChannels = filterAllChannelsNationalByDma(dedupedFeedChannels, genreNationalMapping, dmaForExclusion, genreQueryParams);
      // Hide a channel from the displayed (English) columns when signatureExcluded excludes it from ALL
      // of them — dropping both its ✓ (included) and $ (add-on). On 518 the perDmaOverride lists all 4
      // English tiers → Telemundo drops entirely; a blanket DMA excludes everything → drops; a DMA whose
      // override does NOT cover the displayed packages (or isn't in the overrides) → kept (local shows). [WAT-11301]
      const displayedPackageIds = (toolState.packages || []).map((pkg: any) => pkg?.id).filter(Boolean);
      const newMasterChannelDataObj: ChannelLineupApiData = ChannelLineUpProvider.getFromCmsFeed({
        ...channelData,
        channels: filterChannelsExcludedFromDisplayedPackages(nationalFilteredChannels, dmaForExclusion, signatureExcludedLocalChannels, displayedPackageIds, genreNationalMapping)
      });
      // Generated by Copilot- End - 2026-03-18 10:15:00

      dispatchToolState({
        type: "SET_MASTER_CHANNELS",
        payload: newMasterChannelDataObj.channels
      });

      setApiError(false);
      setShowChnlGrid("display");
      setIsApiLoading(false);
    }, []);

    const handleLocalRsnApiError = () => {
      setApiError(true);
      setShowChnlGrid("display");
      setIsApiLoading(false);
    };

    /** When theres a change in...
   *    - masterChannels (on load or when rsn/locals added)
   *    - channelFilter
   *    - viewMode
   * ...update the channels that are displayed
   * dispatch a reference to the state reducer
   */
    const channelsToDisplay = useMemo(() => {
      const filteredByName = toolState.masterChannels.filter(
        toolState.channelFilter,
        getSortForViewMode(toolState.viewMode)
      );
      // [WAT-11775] The provider's filter matches on channel NAME, so with exactly one of
      // two same-named channels selected it returns BOTH rows while the chip list shows one
      // — a state WAT-11767 newly made reachable, and one no selection could get out of.
      // Narrow to the rows the selection actually identifies. Conservative and fail-open per
      // label: see narrowRowsToSelectedIdentity.
      const newDisplayChannels = narrowRowsToSelectedIdentity(
        filteredByName,
        toolState.channelFilter?.selectedChannel
      );
      dispatchToolState({
        type: "SET_DISPLAY_CHANNELS",
        payload: newDisplayChannels
      });
      return newDisplayChannels;
    }, [toolState.channelFilter, toolState.viewMode, toolState.masterChannels]);

    // [WAT-11766] Re-point already-selected chips at the CURRENT market after a ZIP change.
    //
    // `masterChannels` is rebuilt for the new market by addLocalRsnChnls; the SELECTION lives in
    // toolState.channelFilter and used to survive that rebuild untouched. A local/RSN row's
    // identity here is `label|chnlNum` (the video-lab provider's buildChannelObject emits no
    // matchId), and a channel number is market-specific — so the carried chip identified nothing
    // in the new market. narrowRowsToSelectedIdentity then hit its per-label fail-open and
    // returned EVERY row, which silently reverted WAT-11775: with one of two same-named stations
    // selected, both rendered again.
    //
    // Reconciled against the choice list WITHOUT the selection filter applied — that list is
    // derived from `channelFilter`, so filtering it by the stale selection first would be
    // circular and could hide the very row we need to re-point at.
    useEffect(() => {
      const selectedChannel = toolState.channelFilter?.selectedChannel;
      if (!Array.isArray(selectedChannel) || selectedChannel.length === 0 || !toolState.masterChannels) {
        return;
      }
      const { selectedChannel: _ignoredSelection, ...filterWithoutSelection } = toolState.channelFilter || {};
      const marketChoices = Channels.toChoices(
        toolState.masterChannels.filter(filterWithoutSelection, Channels.sortAlphabetically, true)
      );
      const reconciled = reconcileSearchSelections(selectedChannel, marketChoices);
      // Referential equality is the helper's "nothing changed" contract, so this cannot loop.
      if (reconciled !== selectedChannel) {
        dispatchToolState({
          type: "SET_CHANNEL_FILTER",
          payload: {
            ...toolState.channelFilter,
            selectedChannel: reconciled
          }
        });
      }
    }, [toolState.masterChannels]);

    /** DOM */
    // Generated by Copilot- Start - 2026-03-27 16:40:38
    // Custom tablet breakpoint of 1100 preserves original (min-width:1100px) threshold exactly
    const { isMobile, isTablet, isDesktop, isLargeDesktop, isXLDesktop } = useBreakPoints({ tablet: 1100 });
    // Generated by Copilot- End - 2026-03-27 16:40:38

    const updateSearch = (selectedChannel: any) => {
      dispatchToolState({
        type: "SET_CHANNEL_FILTER",
        payload: {
          ...toolState.channelFilter,
          selectedChannel
        }
      });
      if (selectedChannel?.length > 0) {
        // [WAT-11775] Deduped: two same-named channels being selectable made "espn,espn" reachable.
        const chnsToBeReported = [...new Set(pluckArray(selectedChannel, 'value'))].join();
        fireCustomLinkClickImpressions(chnsToBeReported);
      }
    }
    const _multiSelectInputEvents = {
      handleTagsChange: (selectedChannel: any) => {
        dispatchToolState({
          type: "SET_CHANNEL_FILTER",
          payload: {
            ...toolState.channelFilter,
            selectedChannel
          }
        });
        // dispatchToolState({ type: "SET_DISPLAY_CHANNELS", payload: newChannels });
        if (selectedChannel?.length > 0) {
          const chnsToBeReported = [...new Set(pluckArray(selectedChannel, 'value'))].join()
          fireCustomLinkClickImpressions(chnsToBeReported)
        }
      },
      onBlur: () => {
        Promise.resolve()
      },
      onChange: e => {
        fireSearchImpressions(e.currentTarget.value);
      }
    };

    const PdfLinkSet = () => {
    // In FFP mode, only show myFreeDTV PDF
      if (options?.useFFPMode) {
        return (
        <DtvBox id="pdf-links" sx={{
 display: "inline-flex",
flexWrap: "wrap"
}}>
          {myFreeDTVPdf && myFreeDTVPdf?.label &&
            <DtvButton
              mode={myFreeDTVPdf?.mode || "light"}
              tabIndex={0}
              variant={myFreeDTVPdf?.variant || "uxtext"}
              {...myFreeDTVPdf}
              sx={{
                ...styles.pdfLinkDefaultStyles,
                color: theme && theme?.pdfLinkColor ? `${theme?.pdfLinkColor} !important` : "#2E70BD",
                marginRight: {
 xs: "8px",
sm: "16px"
},
                textDecoration: "none"
              }}>
              <SideArrow />
            </DtvButton>
          }
        </DtvBox>
        );
      }

      const showDefaultPDFs = !channelsQP;
      const showEnglishInterntPDFs = channelsQP && channelsQP === "INTERNET" && (!lang || (lang && lang !== "SPANISH"));
      const showEnglishSatellitePDFs = channelsQP && channelsQP === "SATELLITE" && (!lang || (lang && lang !== "SPANISH"));
      const showSpanishInternetPDFs = channelsQP && channelsQP === "INTERNET" && lang && lang === "SPANISH";
      const showSpanishSatellitePDFs = channelsQP && channelsQP === "SATELLITE" && lang && lang === "SPANISH";
      const showStreamPDFs = domName === 'streamtv';

      return (
      <DtvBox id="pdf-links" sx={{
 display: "inline-flex",
flexWrap: "wrap"
}}>
        {showStreamPDFs && props?.options?.pkg !== "mysportsbeta" ? <>
            {( ((!lang || (lang && lang !== "SPANISH")) && streamChnlsPDFLink) &&
            <DtvButton
            mode={streamChnlsPDFLink?.mode || "light"}
            tabIndex={0}
            variant={streamChnlsPDFLink?.variant || "uxtext"}
            {...streamChnlsPDFLink}
            sx={{
              ...styles.pdfLinkDefaultStyles,
              color: theme && theme?.pdfLinkColor ? `${theme?.pdfLinkColor} !important` : "#2E70BD",
              marginRight: {
 xs: "8px",
sm: "16px"
},
              textDecoration: "none"
            }}>
            <SideArrow />
          </DtvButton>
          )}
          {( ((lang && lang === "SPANISH") && streamSpanishChnlsPDFLink) &&
            <DtvButton
            mode={streamSpanishChnlsPDFLink?.mode || "light"}
            tabIndex={0}
            variant={streamSpanishChnlsPDFLink?.variant || "uxtext"}
            {...streamSpanishChnlsPDFLink}
            sx={{
              ...styles.pdfLinkDefaultStyles,
              color: theme && theme?.pdfLinkColor ? `${theme?.pdfLinkColor} !important` : "#2E70BD",
              marginRight: {
 xs: "8px",
sm: "16px"
},
              textDecoration: "none"
            }}>
            <SideArrow />
          </DtvButton>
          )}
        </> : <>

        {(showDefaultPDFs || (showEnglishInterntPDFs && props?.options?.pkg !== "mysportsbeta")) && internetChnlsPDFLink &&
          <DtvButton
            mode={internetChnlsPDFLink?.mode || "light"}
            tabIndex={0}
            variant={internetChnlsPDFLink?.variant || "uxtext"}
            {...internetChnlsPDFLink}
            sx={{
              ...styles.pdfLinkDefaultStyles,
              color: theme && theme?.pdfLinkColor ? `${theme?.pdfLinkColor} !important` : "#2E70BD",
              marginRight: {
 xs: "8px",
sm: "16px"
},
              textDecoration: "none"
            }}>
            <SideArrow />
          </DtvButton>
        } {(showDefaultPDFs || showEnglishSatellitePDFs) && satelliteChnlsPDFLink &&
          <DtvButton
            mode={satelliteChnlsPDFLink?.mode || "light"}
            tabIndex={0}
            variant={satelliteChnlsPDFLink?.variant || "uxtext"}
            {...satelliteChnlsPDFLink}
            sx={{
              ...styles.pdfLinkDefaultStyles,
              color: theme && theme?.pdfLinkColor ? `${theme?.pdfLinkColor} !important` : "#2E70BD",
              textDecoration: "none",
              marginRight: {
 xs: "16px",
sm: "24px"
}
            }}>
            <SideArrow />
          </DtvButton>
        } {showSpanishInternetPDFs && internetSpanishChnlsPDFLink &&
          <DtvButton
            mode={internetSpanishChnlsPDFLink?.mode || "light"}
            tabIndex={0}
            variant={internetSpanishChnlsPDFLink?.variant || "uxtext"}
            sx={{
 ...styles.pdfLinkDefaultStyles,
              color: theme && theme?.pdfLinkColor ? `${theme?.pdfLinkColor} !important` : "#2E70BD"
             }}
            {...internetSpanishChnlsPDFLink}
          />
        } {showSpanishSatellitePDFs && satelliteSpanishChnlsPDFLink &&
          <DtvButton
            mode={satelliteSpanishChnlsPDFLink?.mode || "light"}
            tabIndex={0}
            variant={satelliteSpanishChnlsPDFLink?.variant || "uxtext"}
            sx={{
 ...styles.pdfLinkDefaultStyles,
              color: theme && theme?.pdfLinkColor ? `${theme?.pdfLinkColor} !important` : "#2E70BD"
             }}
            {...satelliteSpanishChnlsPDFLink}
          />
        } {(mySportsBetaPDFLink && mySportsBetaPDFLink?.label && props?.options?.pkg === "mysportsbeta") &&
          <DtvButton
            mode={mySportsBetaPDFLink?.mode || "light"}
            tabIndex={0}
            variant={mySportsBetaPDFLink?.variant || "uxtext"}
            {...mySportsBetaPDFLink}
            sx={{
              ...styles.pdfLinkDefaultStyles,
              color: theme && theme?.pdfLinkColor ? `${theme?.pdfLinkColor} !important` : "#2E70BD",
              marginRight: {
 xs: "8px",
sm: "16px"
},
              textDecoration: "none"
            }}>
            <SideArrow />
          </DtvButton>
        }

        </>}
      </DtvBox>
      );
    }

    const getFilterSecContent = () => {
      const FilterLegalBlock = () => (
      <DtvBox id="legal" sx={{
 margin: "0px",
width: "100%"
}}>
        <DtvText
          content={legalInNationalTab.legalShort_Text}
          variant="smalltext"
          sx={{
            display: "block",
            color: theme ? `${theme?.subheadingTextColor} !important` : "#102641 !important",
            fontSize: "14px !important",
            "& > p": {
              marginBottom: "20px",
              marginTop: '20px',
              [breakpoints.between(breakpoints.values.sm, breakpoints.values.md)]: {
                marginBottom: "24px",
                marginTop: '12px'
              },
              [breakpoints.down(breakpoints.values.sm)]: {
                marginBottom: "32px",
                marginTop: '20px'
              }
            }
          }}
        />
      </DtvBox>
      );

      const FilterSelectBlock = () => (
      <DtvGrid item
        id="FilterSelectBlock"
        lg={4} md={4} sm={5} xs={12}
        sx={{
              ...styles.sortDropdown,
              backgroundColor: theme && theme?.backgroundColor ? `${theme?.backgroundColor} !important` : "#fff",
              zIndex: 1000
            }}>
        <SelectDropdown
          data-testid="selectComponent"
          id="sort-dropdown"
          prefix={commonData.sortAndFilterSortByLabel || commonData.sortByLabel || "Sort By:"}
          value={selectedValue}
          options={
            sortOptions && sortOptions.map((op: KeyValuePair) => ({
              key: op.key,
              label: op.value
            }))
          }
          onChange={changeViewMode}
        />
      </DtvGrid>
      );

      try {
        if (isChannelLineUpPage === false) {
          return (
          <DtvGrid
          container item
          alignItems="center"
          direction="row" justifyContent="flex-start"
          sx={{
 ...styles.customPadding,
...styles.filterSection
}}>
            {(isDesktop || isLargeDesktop || isXLDesktop) ? (
              <>
              <FilterLegalBlock />
              <DtvBox id="legal+Select" sx={{
 ...styles.filterSectionFlexDiv,
paddingTop: "0px !important"
}}>
                <FilterSelectBlock />
              </DtvBox>
              </>
            ) : isTablet ? (
              <>
              <FilterLegalBlock />
              <DtvBox id="link+Select" sx={{
 ...styles.filterSectionFlexDiv,
paddingTop: "0px",
pb: "0px !important"
}}>
                <FilterSelectBlock />
              </DtvBox>
              </>
            ) : (
              <>
              <FilterSelectBlock />
              <DtvBox id="legal+Pdf" sx={{
 ...styles.filterSectionFlexDiv,
paddingLeft: "0px !important"
}}>
                <FilterLegalBlock />
              </DtvBox>
              </>
            )}
          </DtvGrid>
          );
        }
      } catch (error) {
        console.log("Error rendering getFilterSecContent >>", error);
        return <></>;
      }
    };

    const isErrorData = useMemo(() => (!isModalSrcValid || !isChannelsQPValid || !isLangQPValid || !isPkgQPValid || !isCmprPkgQPValid ||
      channelData.packages.length === 0 ||
      (lang === "SPANISH" && !channelsQP)
    ), [toolState.packages]);

    const isReadyForTable = useMemo(() => (!isComputing || (!props?.options?.pkg && !props?.options?.lang)) && toolState.packages.length > 0, [isComputing, toolState.packages]);

    console.log(`ChnlCompareTool-v3 rendering...\n`,
      'toolState: ', toolState,
      '\nisErrorData', isErrorData,
      '\nisReadyForTable: ', isReadyForTable,
      '\nviewport is: ', isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
      '\npkgQP: ', pkg,
      '\nchannelsQP: ', channelsQP ? channelsQP : undefined,
      '\nisApiLoading: ', isApiLoading);

    /** Start Render */
    if (isErrorData) {
      console.log(`ChnlCompareTool-v3 ERROR...\n`,
        '!isModalSrcValid: ', !isModalSrcValid,
        '\n!isChannelsQPValid', !isChannelsQPValid,
        '\n!isLangQPValid: ', !isLangQPValid,
        '\n!isPkgQPValid: ', !isPkgQPValid,
        '\n!isCmprPkgQPValid: ', !isCmprPkgQPValid,
        '\ntoolState.packages.length: ', toolState.packages?.length);
      return (
      <PdfLinksErrorDisplay
        channelsQP={channelsQP}
        errorMessageForModal={commonData?.errorMessageForModal}
        headline={commonData?.headline}
        internetChnlsPDFLink={commonData?.internetChnlsPDFLink}
        internetHeadline={commonData?.internetHeadline}
        internetSpanishChnlsPDFLink={commonData?.internetSpanishChnlsPDFLink}
        isChannelsQPValid={isChannelsQPValid}
        isCmprPkgQPValid={isCmprPkgQPValid}
        isErrorScenario={isErrorScenario}
        isLangQPValid={isLangQPValid}
        isModalSrcValid={isModalSrcValid}
        isPkgQPValid={isPkgQPValid}
        packages={commonData?.packages}
        satelliteChnlsPDFLink={commonData?.satelliteChnlsPDFLink}
        satelliteHeadline={commonData?.satelliteHeadline}
        satelliteSpanishChnlsPDFLink={commonData?.satelliteSpanishChnlsPDFLink}
      />
      );
    }
    const inPagePadding = props?.options?.inPage === "true" ? {
      padding: {
        xs: "24px 20px",
        sm: "54px 32px"
      }
    } : {};
    try {
      return (
      <DtvBox
        id="channel-compare-tool-v3"
        style={{
          backgroundColor: theme && theme?.backgroundColor ? theme?.backgroundColor : colorPalette.neutral_white.main,
          height: "100%",
          width: "100%"
        }}
        sx={{
          position: 'relative',
          ...inPagePadding
        }}>
        {isChannelLineUpPage === false && (
          <DtvText
            id="channel-lineup-headline" // "channellineup-headline"
            variant="h2"
            content={
              // Support myFreeDTV labels from FFP mode
              myFreeDTVChnls?.chnlLineupLabel ||
              (props?.options?.qp == null && channelsQP === "INTERNET" ?
              commonData?.headline || "DIRECTV Channel Lineup" :
              isChannelsQPValid && channelsQP && channelsQP === "INTERNET" ?
              commonData?.internetHeadline || "DIRECTV Channel Lineup" :
              isChannelsQPValid && channelsQP && channelsQP === "SATELLITE" ?
              commonData?.satelliteHeadline || "DIRECTV Satellite Channel Lineup" :
              commonData?.headline || "DIRECTV Channel Lineup")
            }
            sx={{
 ...styles.headline,
color: theme ? theme?.headerTextColor : "#102641",
backgroundColor: theme ? theme?.backgroundColor : colorPalette.neutral_white.main
}}
          />
        )}
        {isChannelLineUpPage === false && (
        <DtvBox id="navigation-header" sx={{
 mx: '2px',
padding: {
 xs: "0 20px",
sm: "0"
}
}}>
          <DtvBox sx={{
 ...styles.customPadding,
marginBottom: "10px"
}}>
            {apiError && (
              <Alert severity="error">
                {localAndRegionalChannels.errorMsgForServiceFail ||
                  "Please try again after sometime"}
              </Alert>
            )}
            <DtvText
              gutterBottom={true}
              variant="body1"
              content={
                // Support myFreeDTV subtext from FFP mode
                myFreeDTVChnls?.chnlSubText ||
                (options?.isLocal === "true" ?
                  contentText?.nationalAndRegionalChannelLabelDesc ||
                    commonData?.nationalAndRegionalChannelLabelDesc ||
                    "Find national favorites or see what regional sports & local channels we offer." :
                  "Find national favorites or see what regional sports channels we offer")
              }
              sx={{
                ...styles.nationalTitle,
                color: theme ? `${theme?.subheadingTextColor} !important` : "#102641"
              }}
            />
          </DtvBox>

          {!options?.useFFPMode && <ZipcodeModule
            ciConfig={zipcodeModuleConfig}
            id={`${id}-zip-module`}
            initData={urlZipcode ? { zipcode: urlZipcode } : null}
            isConditional={!!urlZipcode}
            mode={theme?.zipCodeModuleMode}
            position="left"
            size="small"
            localRsnApiOptions={{
              stream: useStream,
              satellite: useSatellite
            }}
            onLocalRsnChnlsApiError={handleLocalRsnApiError}
            onLocalRsnChnlsConfirmed={addLocalRsnChnls}
            onSearchButtonClick={setLoadingLocalRsnsChannels}
          />}

          <DtvBox sx={{
 ...styles.customPadding,
position: "relative",
marginTop: "30px"
}}>
            <ChannelSearch
              id={`${id}-channel-search`}
              {...searchObj}
              theming={'light'}
              type="chnlupModal"
              updateSearchEvents={updateSearch}
              // [WAT-11775] Subscriptions NORMALISED before they reach the search box. They
              // were spread in raw, and an embeddedSvods entry carries `name` with no
              // `displayText`/`value` — so the typeahead, which reads the label, offered none
              // of them and typing a subscription's name found nothing. Pre-WAT-11767 this
              // path threw on them; that PR's optional chaining turned the crash into a
              // silent drop, which is why it went unnoticed. The correct shape was already
              // written five lines below, in the commented-out MultiChnlSearch sibling.
              channels={[...toolState.masterChannels ?
                  Channels.toChoices(
                      toolState.masterChannels.filter(toolState.channelFilter, Channels.sortAlphabetically, true)
                    ) :
                  [], ...(filteredEmbeddedSvods || []).map((item: any) => ({
                    ...item,
                    displayText: item?.displayText ?? item?.name,
                    value: item?.value ?? item?.name,
                    type: "svod"
                  }))]}
            />
            {/* <MultiChnlSearch
              id={`${id}-multi-search`}
              selectedValue={selectedValue}
              events={multiSelectInputEvents}
              limitTags={commonData.limitTags || 10}
              limitAlertText={commonData?.limitAlertText}
              choices={
                toolState.masterChannels
                  ? Channels.toChoices(
                      toolState.masterChannels.filter(toolState.channelFilter, Channels.sortAlphabetically, true)
                    )
                  : []
              }
              embeddedSvodsChoice={
                filteredEmbeddedSvods && filteredEmbeddedSvods.length > 0
                  ? filteredEmbeddedSvods.map(item => ({
                      displayText: item.name,
                      value: item.name,
                      type: "svods"
                    }))
                  : []
              }
              searchLabel={contentText.searchLabel || commonData.searchLabel ||
                "Search channels"
              }
              linkText={commonData.clearSearchLinkText ||
                "Reset Search"
              }
              errorText={commonData.searchNoMatchInputMsg ||
                "Hmm... cannot find that one"
              }
              groupTitle={commonData.searchMultiSelectionGroupTitle ||
                "All channels including"
              }

            /> */}
          </DtvBox>
          <>
            {getFilterSecContent()}
          </>
        </DtvBox>
        )}
        {showyMySportsbetaInEligibleMessage &&
          <DtvBox sx={{
            margin: {
              xs: "20px 20px 0 20px",
              sm: "32px 20px 0 32px",
              md: "32px 32px 0 32px"
            },
            textAlign: "center"
          }}>
                <DtvText
                  content={channelData?.localAndRegionalChannels?.mySportsBetaErrorMessage}
                  variant="body1medium"
                  sx={{
                    color: theme ? theme?.subheadingTextColor : colorPalette?.jet?.main,
                    textDecoration: "none",
                    textAlign: "center"
                  }}
                  />
                  {/* <DtvText
                  variant="h4"
                  content={ "Please try another ZIP Code. "}
                  sx={{
                    color: colorPalette?.jet?.main,
                    textDecoration : "none",
                    textAlign: "center"
                  }}
                  /> */}
          </DtvBox>
        }
        <DtvBox sx={{
          mx: '2px',
          background: "#fbfbfb"
        }}>
            {/* Dual Rendering Mode: Support both ChnlComparisonGrid (v2) and NationalChnlTable (FFP) */}
            {isReadyForTable && showChnlGrid === "display" && renderMode === 'grid' && <ChnlComparisonGrid
              changeViewModeFromNationalTable={changeViewModeFromNationalTable}
              channelCategories={toolState.channelCategories}
              channelCategoryMembership={channelData?.channelCategoryMembership}
              channelFilter={toolState.channelFilter?.selectedChannel}
              channels={channelsToDisplay}
              channelsQP={channelsQP}
              cmprPkg={cmprPkg}
              commonData={commonData}
              customPackage={customPkg}
              customPkgIndex={customPkgIndex}
              edspFlow={edspFlow}
              embeddedSvods={updateSvodFilterData}
              expanded={toolState.channelFilter?.selectedChannel?.length > 0}
              getOffersDTViEmbeddedIncludedSvods={channelDataWithSvods?.getOffersDTViEmbeddedIncludedSvods}
              getOffersDTVsEmbeddedIncludedSvods={channelDataWithSvods?.getOffersDTVsEmbeddedIncludedSvods}
              id={`${id}-channel-grid`}
              isChannelLineUpPage={isChannelLineUpPage}
              isEmbeddedSvodsEnabled={channelDataWithSvods?.isEmbeddedSvodsEnabled}
              lang={lang}
              localMySportsGenreChannels={localMySportsGenreChannels}
              modalSrc={modalSrc}
              modalView={(pageName || "").includes("modal")}
              msbNationalRollOut={msbNationalRollOut}
              packageBgColor={options?.packageBgColor}
              packageCategorySelected={toolState.channelFilter.categoryId}
              packages={toolState.packages}
              rsnChnlsObj={rsnChnlsObj}
              rsnTierLegalTextObj={options?.rsnTierLegalTextObj}
              showCTA={options?.showCTA}
              showCtaCI={channelData?.showCtaCI}
              sortOptions={sortOptions}
              theme={theme}
              viewMode={toolState.viewMode}
              onEmbeddedSvodsFiltered={handleEmbeddedSvodsFiltered}
            />}

            {/* FFP Mode: NationalChnlTable rendering (from channel-lineup-ffp) */}
            {isReadyForTable && showChnlGrid === "display" && renderMode === 'table' && <NationalChnlTable
              activeTab={activeTab}
              changeViewModeFromNationalTable={changeViewModeFromNationalTable}
              channelCategories={toolState.channelCategories}
              channels={channelsToDisplay}
              channelsQP={channelsQP}
              cmprPkg={cmprPkg}
              commonData={commonData}
              customPackage={customPkg}
              customPkgIndex={customPkgIndex}
              edspFlow={edspFlow}
              expanded={toolState.channelFilter?.selectedChannel?.length > 0}
              id={`${id}-channel-table`}
              isChannelLineUpPage={isChannelLineUpPage}
              lang={lang}
              modalSrc={modalSrc}
              modalView={(pageName || "").includes("modal")}
              myFreeDTVChnls={myFreeDTVChnls}
              packageBgColor={options?.packageBgColor}
              packageCategorySelected={toolState.channelFilter.categoryId}
              packages={toolState.packages}
              pkgHeight={null}
              serviceLogoPath={toolState.channelDataObjects?.service?.logo}
              serviceNameLabel={toolState.channelDataObjects?.service?.name}
              showCTA={options?.showCTA}
              showCtaCI={channelData?.showCtaCI}
              sortOptions={sortOptions}
              viewMode={toolState.viewMode}
            />}
        </DtvBox>
        <>
          <PdfLinkSet />
        </>
      </DtvBox>
      );
    } catch (error) {
      console.log("Error rendering Channel Compare Tool v3 >>", new Error(error));
      try {
        return <PdfLinksErrorDisplay
        channelsQP={channelsQP}
        errorMessageForModal={commonData?.errorMessageForModal}
        headline={commonData?.headline}
        internetChnlsPDFLink={commonData?.internetChnlsPDFLink}
        internetHeadline={commonData?.internetHeadline}
        internetSpanishChnlsPDFLink={commonData?.internetSpanishChnlsPDFLink}
        isChannelsQPValid={isChannelsQPValid}
        isCmprPkgQPValid={isCmprPkgQPValid}
        isErrorScenario={isErrorScenario}
        isLangQPValid={isLangQPValid}
        isModalSrcValid={isModalSrcValid}
        isPkgQPValid={isPkgQPValid}
        packages={commonData?.packages}
        satelliteChnlsPDFLink={commonData?.satelliteChnlsPDFLink}
        satelliteHeadline={commonData?.satelliteHeadline}
        satelliteSpanishChnlsPDFLink={commonData?.satelliteSpanishChnlsPDFLink}
      />;
      } catch (error) {
        console.log("Fatal Error rendering Channel Compare Tool >>", new Error(error));
        // return <ErrorDisplay error={error} component="Channel Compariosn Tool"/>;
        return <></>;
      }
    }
  } catch (error) {
    console.log("Logic Error in Channel Compare Tool >>", new Error(error));
    // return <ErrorDisplay error={error} component="Channel Compariosn Tool"/>;
    return <></>;
  }
}
